import {
  PROVIDER_STATE_LABELS,
  TRACKER_KEY_NAMES,
  type ProviderDiagnostic,
  type ProviderDiagnosticsResponse,
  type ProviderKeySource,
  type ProviderKeyStatus,
  type ProviderState,
} from '@studio/shared';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../../shell/api.js';
import { Failure } from '../../shell/EmptyState.js';
import type { Surface } from '../../shell/router.js';
import { useAsync } from '../../shell/useAsync.js';
import { GenerationInstructionsSettings } from '../generation/Instructions.js';

/**
 * Provider diagnostics and provider keys, for whoever runs this Studio.
 *
 * It answers two questions - which provider can generate here, and why not - and it
 * is where the keys are kept: saved here, a key takes effect at once and wins over
 * the deployment environment. A value is shown only when the operator presses Show,
 * and is read from the server at that moment rather than carried with the page.
 *
 * The Tracker's address and token are kept on the same page, in the same rows. The
 * Tracker generates nothing, so it has a card of its own and is not counted above.
 */

const ORDER: readonly ProviderState[] = [
  'ready',
  'needsLogin',
  'apiKeySignIn',
  'limitReached',
  'clientMissing',
  'incompatible',
  'unavailable',
  'missingKey',
  'disabled',
];

function stateRank(state: ProviderState): number {
  const index = ORDER.indexOf(state);
  return index < 0 ? ORDER.length : index;
}

function operationLabel(operation: string): string {
  switch (operation) {
    case 'imageGenerate':
      return 'изображения';
    case 'imageEdit':
      return 'правки изображений';
    case 'videoGenerate':
      return 'видео';
    case 'planText':
      return 'текст планирования';
    default:
      return operation;
  }
}

/**
 * What a provider documents and this Studio does not hold.
 *
 * The names are the Studio's own vocabulary, so the page can say them in Russian and
 * the source stays a link the operator can open and check.
 */
const CAPABILITY_LABELS: Readonly<Record<string, string>> = {
  imageEdit: 'правки изображений',
  imageGenerateReferences: 'генерацию по reference-изображениям',
  videoGenerate: 'видео',
  videoEdit: 'правки видео',
};

function capabilityLabel(capability: string): string {
  return CAPABILITY_LABELS[capability] ?? capability;
}

const SOURCE_LABELS: Readonly<Record<ProviderKeySource, string>> = {
  studio: 'сохранено в Studio',
  environment: 'из окружения',
  unreadable: 'сохранено, не удалось расшифровать',
  missing: 'не задано',
};

function failureOf(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : 'Не удалось связаться со Studio.';
}

type Mode = 'idle' | 'editing' | 'confirmRemove';

/**
 * Choosing the subscription image source, and giving it back.
 *
 * Both directions change what the next spread does, so each is one press and one
 * confirmation that says what changes. Work already queued keeps the source it was
 * started with. Signing Codex in is a server step, and the card says which one.
 */
function ImageSourceChoice({
  provider,
  chosen,
  onChange,
}: {
  provider: ProviderDiagnostic;
  chosen: boolean;
  onChange: (next: ProviderDiagnosticsResponse) => void;
}): ReactNode {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function choose(providerId: string | null): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onChange(await api.chooseImageSource(providerId));
      setConfirming(false);
    } catch (caught) {
      setError(failureOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="provider-key">
      {confirming ? (
        <div className="provider-key-actions">
          <span className="note">
            {chosen
              ? 'Иконки, feature graphics и скриншоты снова идут через ключи провайдеров и Mock. Уже поставленная в очередь работа завершится на Codex.'
              : `Иконки, feature graphics и скриншоты будут делаться только через ${provider.label}: без отката на ключ или Mock, стоимость не сообщается. Кадры набора создаются последовательно; preview videos и Bulk Runs с генерацией выключены, пока не переключите обратно.`}
          </span>
          <button type="button" className="secondary" disabled={busy} onClick={() => void choose(chosen ? null : provider.providerId)}>
            {busy ? 'Сохраняю…' : chosen ? 'Перестать использовать' : 'Использовать'}
          </button>
          <button type="button" className="ghost" disabled={busy} onClick={() => setConfirming(false)}>
            Отмена
          </button>
        </div>
      ) : (
        <div className="provider-key-actions">
          <button type="button" className={chosen ? 'ghost' : 'secondary'} onClick={() => setConfirming(true)}>
            {chosen ? 'Перестать использовать для изображений…' : 'Использовать для изображений…'}
          </button>
        </div>
      )}
      {error !== null ? (
        <p className="login-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ProviderKey({
  status,
  noun = 'ключ',
  plain = false,
  onChange,
}: {
  status: ProviderKeyStatus;
  /** What this row's value is called in its own copy. */
  noun?: string;
  /** Not a secret: typed into a field that shows it, and shown as soon as there is one. */
  plain?: boolean;
  onChange: (next: ProviderDiagnosticsResponse) => void;
}): ReactNode {
  const { name, source } = status;
  const readable = source === 'studio' || source === 'environment';
  const [shown, setShown] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>(readable ? 'idle' : 'editing');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saves, setSaves] = useState(0);

  // The diagnostics carry no value, plain or not, so a plain one is read here: when the
  // row appears with something to show, and again after every save. A save is counted
  // rather than told by what it cleared: a read that failed left nothing shown to clear.
  useEffect(() => {
    if (!plain || !readable || shown !== null) return;
    let live = true;
    api.readProviderKey(name).then(
      (key) => {
        if (live) setShown(key.value);
      },
      (caught: unknown) => {
        if (live) setError(failureOf(caught));
      },
    );
    return () => {
      live = false;
    };
  }, [plain, readable, name, shown, saves]);

  async function run(work: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (caught) {
      setError(failureOf(caught));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (): Promise<void> =>
    run(async () => {
      if (shown !== null) {
        setShown(null);
        return;
      }
      setShown((await api.readProviderKey(name)).value);
    });

  const save = (event: FormEvent): Promise<void> => {
    event.preventDefault();
    return run(async () => {
      const next = await api.saveProviderKey(name, draft);
      setDraft('');
      setShown(null);
      setSaves((count) => count + 1);
      setMode('idle');
      onChange(next);
    });
  };

  const remove = (): Promise<void> =>
    run(async () => {
      const next = await api.removeProviderKey(name);
      setShown(null);
      setMode('idle');
      onChange(next);
    });

  return (
    <div className="provider-key" role="group" aria-label={name} data-source={source}>
      <div className="provider-key-head">
        <code>{name}</code>
        <span className="note">{SOURCE_LABELS[source]}</span>
      </div>
      {readable ? (
        <div className="provider-key-value">
          <code className={shown === null ? 'masked' : undefined} aria-label={`Значение ${name}`}>
            {shown ?? (plain ? '…' : '••••••••••••••••')}
          </code>
          {plain ? null : (
            <button type="button" className="ghost" disabled={busy} onClick={() => void toggle()}>
              {shown === null ? 'Показать' : 'Скрыть'}
            </button>
          )}
        </div>
      ) : null}
      {mode === 'editing' ? (
        <form className="provider-key-form" onSubmit={(event) => void save(event)}>
          <input
            type={plain ? 'text' : 'password'}
            autoComplete="off"
            spellCheck={false}
            aria-label={`Новое значение для ${name}`}
            placeholder={readable ? `Вставьте новый ${noun}` : `Вставьте ${noun}`}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <button type="submit" className="primary" disabled={busy || draft.trim() === ''}>
            {busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
          {readable ? (
            <button type="button" className="ghost" disabled={busy} onClick={() => setMode('idle')}>
              Отмена
            </button>
          ) : null}
        </form>
      ) : null}
      {mode === 'confirmRemove' ? (
        <div className="provider-key-actions">
          <span className="note">Удалить сохранённый {noun}? Значение из окружения, если есть, снова вступит в силу.</span>
          <button type="button" className="secondary" disabled={busy} onClick={() => void remove()}>
            {busy ? 'Удаляю…' : 'Удалить'}
          </button>
          <button type="button" className="ghost" disabled={busy} onClick={() => setMode(readable ? 'idle' : 'editing')}>
            Keep
          </button>
        </div>
      ) : null}
      {mode === 'idle' ? (
        <div className="provider-key-actions">
          <button type="button" className="secondary" onClick={() => setMode('editing')}>
            {source === 'studio' ? 'Заменить' : `Сохранить ${noun} здесь`}
          </button>
          {source === 'studio' ? (
            <button type="button" className="ghost" onClick={() => setMode('confirmRemove')}>
              Удалить…
            </button>
          ) : null}
        </div>
      ) : null}
      {source === 'unreadable' && mode === 'editing' ? (
        <button type="button" className="ghost" disabled={busy} onClick={() => setMode('confirmRemove')}>
          Удалить нечитаемый {noun}…
        </button>
      ) : null}
      {error !== null ? (
        <p className="login-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function Provider({
  imageSource,
  provider,
  onChange,
}: {
  /** The chosen subscription image source, or null. */
  imageSource: string | null;
  provider: ProviderDiagnostic;
  onChange: (next: ProviderDiagnosticsResponse) => void;
}): ReactNode {
  const abilities = provider.models.flatMap((model) => model.operations);
  const polling = abilities.some((operation) => operation.polling);
  const idempotent = abilities.some((operation) => operation.idempotency);
  const subscription = provider.accounting === 'subscription';
  // A subscription route has no price by design; it is not a route nobody priced.
  const unpriced = subscription ? [] : abilities.filter((operation) => !operation.priced);

  return (
    <article className="card" aria-labelledby={`provider-${provider.providerId}`}>
      <header className="card-head">
        <h2 id={`provider-${provider.providerId}`}>{provider.label}</h2>
        <span className={`badge state-${provider.state}`} data-state={provider.state}>
          {PROVIDER_STATE_LABELS[provider.state]}
        </span>
      </header>
      <p className="note">{provider.note}</p>
      <dl className="facts">
        <div>
          <dt>Может генерировать</dt>
          <dd>{provider.operations.length === 0 ? 'ничего не заявлено' : provider.operations.map(operationLabel).join(', ')}</dd>
        </div>
        {provider.keys.length === 0 ? (
          <div>
            <dt>Ключи</dt>
            <dd>не нужны</dd>
          </div>
        ) : null}
        {subscription ? (
          <>
            <div>
              <dt>Стоимость</dt>
              <dd>по подписке, не сообщается</dd>
            </div>
            <div>
              <dt>Запуски</dt>
              <dd>одно изображение за запуск; кадры набора по очереди, без самоповтора</dd>
            </div>
          </>
        ) : (
          <>
            <div>
              <dt>Долгие задачи</dt>
              <dd>{polling ? 'с опросом и отменой' : 'ответ одним вызовом'}</dd>
            </div>
            <div>
              <dt>Безопасность повтора</dt>
              <dd>{idempotent ? 'идемпотентная отправка' : 'без идемпотентной отправки'}</dd>
            </div>
          </>
        )}
      </dl>
      {subscription ? <ImageSourceChoice provider={provider} chosen={imageSource === provider.providerId} onChange={onChange} /> : null}
      {provider.unsupported.length === 0 ? null : (
        <p className="note" data-testid="unsupported">
          {`Провайдер это умеет, а этот адаптер — нет: ${provider.unsupported
            .map((entry) => `${capabilityLabel(entry.capability)} (${entry.source})`)
            .join('; ')}.`}
        </p>
      )}
      {provider.keys.map((status) => (
        <ProviderKey key={`${status.name}:${status.source}`} status={status} onChange={onChange} />
      ))}
      {unpriced.length > 0 ? <p className="note">Для {unpriced.map((operation) => operationLabel(operation.operation)).join(', ')} цена не опубликована, поэтому это не выбирается для плана.</p> : null}
    </article>
  );
}

/**
 * How each Tracker row is worded. The address is not a secret, so it is typed and shown
 * in the open; the token is handled exactly as a provider key is.
 */
const TRACKER_ROWS: Readonly<Record<(typeof TRACKER_KEY_NAMES)[number], { noun: string; plain: boolean }>> = {
  TRACKER_API_URL: { noun: 'адрес', plain: true },
  TRACKER_API_TOKEN: { noun: 'токен', plain: false },
};

function Tracker({ rows, onChange }: { rows: readonly ProviderKeyStatus[]; onChange: (next: ProviderDiagnosticsResponse) => void }): ReactNode {
  const unreadable = rows.filter((row) => row.source === 'unreadable').map((row) => row.name);
  return (
    <article className="card" aria-labelledby="tracker-card">
      <header className="card-head">
        <h2 id="tracker-card">Tracker</h2>
      </header>
      <p className="note">
        Pop Traffic Tracker, из которого Studio получает App и их Custom Store Listings. Нужны его адрес и токен каталога,
        который выпускается в Settings самого Tracker. Сохранённые здесь, они действуют сразу и перекрывают окружение
        развёртывания.
      </p>
      {unreadable.length > 0 ? (
        <p className="note">
          Сохранённые {unreadable.join(' и ')} нельзя расшифровать после смены SESSION_SECRET. Сохраните значение снова или
          удалите его.
        </p>
      ) : null}
      {TRACKER_KEY_NAMES.map((name) => {
        const status = rows.find((row) => row.name === name);
        return status === undefined ? null : (
          <ProviderKey key={`${name}:${status.source}`} status={status} {...TRACKER_ROWS[name]} onChange={onChange} />
        );
      })}
    </article>
  );
}

function Providers(): ReactNode {
  const loaded = useAsync(async () => api.readProviders(), []);
  const { reload, set } = loaded;

  if (loaded.error !== null) {
    return (
      <section className="page">
        <p className="eyebrow">Providers</p>
        <Failure message={loaded.error} onRetry={reload} />
      </section>
    );
  }
  if (loaded.value === null) return <section className="page" aria-busy="true" />;

  const providers = [...loaded.value.providers].sort((left, right) => stateRank(left.state) - stateRank(right.state));
  const ready = providers.filter((provider) => provider.state === 'ready');
  const counted = providers.filter((provider) => provider.state !== 'disabled');
  const source = providers.find((provider) => provider.providerId === loaded.value?.imageSource);

  return (
    <section className="page">
      <p className="eyebrow">Providers</p>
      <h1>Где идёт генерация</h1>
      <GenerationInstructionsSettings />
      <p className="note">
        {ready.length} из {counted.length} настроены здесь. Встроенный путь Mock не требует ключа, поэтому Studio
        работает и при пустых ключах. Ключ, сохранённый здесь, действует сразу и перекрывает окружение развёртывания.
      </p>
      <p className="note">
        Статус значит, что ключ задан и провайдер подключён к этому развёртыванию. Ни один удалённый вызов при
        открытии страницы не выполняется: доступность сервиса не проверяется, и генерация не запускается.
      </p>
      {loaded.value.imageSource === null ? null : (
        <p className="note" data-testid="image-source">
          {`Иконки, feature graphics и скриншоты делаются только через ${source?.label ?? loaded.value.imageSource}. Отката на другого провайдера нет, а Bulk Runs с генерацией выключены.`}
        </p>
      )}
      <div className="card-grid">
        {providers.map((provider) => (
          <Provider key={provider.providerId} imageSource={loaded.value?.imageSource ?? null} provider={provider} onChange={set} />
        ))}
      </div>
      <div className="card-grid">
        <Tracker rows={loaded.value.tracker} onChange={set} />
      </div>
    </section>
  );
}

export const surface: Surface = {
  id: 'settings-providers',
  path: '/settings/providers',
  title: 'Providers',
  render: () => <Providers />,
};
